import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to reset password. The link may have expired.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white border-4 border-black rounded-3xl p-8 w-full max-w-md shadow-brutal-lg text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Password Reset!</h1>
          <p className="text-slate-600 mb-6">
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (!token || error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white border-4 border-black rounded-3xl p-8 w-full max-w-md shadow-brutal-lg text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
          <p className="text-slate-600 mb-6">
            {error || 'This password reset link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
          >
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white border-4 border-black rounded-3xl p-8 w-full max-w-md shadow-brutal-lg">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold">Create New Password</h1>
          <p className="text-slate-600 mt-2">
            Enter your new password below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="font-bold text-slate-900">New Password</Label>
            <Input
              id="password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Min 6 characters"
              className="mt-1 h-12 border-2 border-black rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="font-bold text-slate-900">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Re-enter your password"
              className={`mt-1 h-12 border-2 rounded-xl ${
                formData.confirmPassword && formData.password !== formData.confirmPassword 
                  ? 'border-red-500 bg-red-50' 
                  : formData.confirmPassword && formData.password === formData.confirmPassword
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-black'
              }`}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <p className="text-xs text-red-600 mt-1">✗ Passwords do not match</p>
            )}
            {formData.confirmPassword && formData.password === formData.confirmPassword && (
              <p className="text-xs text-emerald-600 mt-1">✓ Passwords match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || formData.password !== formData.confirmPassword}
            className="w-full h-12 bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/auth')}
            className="text-sm text-slate-600 hover:text-indigo-600"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
